<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import {
  fetchWhatsAppProfile,
  updateWhatsAppProfile,
} from '../../../../../api/inbox/whatsappProfile';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    SettingsSection,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      profile: {},

      // Editable fields
      profilePicture: null,
      profilePicturePreview: null,
      businessAddress: '',
      businessEmail: '',
      businessAbout: '',
      businessDescription: '',
      maxAboutLength: 139,
      maxDescriptionLength: 512,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    selectedTabKey() {
      return this.$route.query.tab || 'profile';
    },
    descriptionCharacterCount() {
      return this.businessDescription.length;
    },
    descriptionWarning() {
      if (this.descriptionCharacterCount > this.maxDescriptionLength) {
        return this.$t('INBOX_MGMT.WHATSAPP_PROFILE.DESCRIPTION_TOO_LONG');
      }
      return '';
    },
  },
  watch: {
    inbox() {
      this.fetchProfileData();
    },
  },
  mounted() {
    this.fetchProfileData();
  },
  methods: {
    async fetchProfileData() {
      this.isLoading = true;
      try {
        const data = await fetchWhatsAppProfile(this.accountId, this.inbox.id);
        this.profile = data;

        // Populate editable fields
        this.businessAddress = data.business_address || '';
        this.businessEmail = data.business_email || '';
        this.businessAbout = data.business_about || '';
        this.businessDescription = data.business_description || '';

        // Reset profile picture preview on data fetch
        this.profilePicture = null;
        this.profilePicturePreview = null;
      } catch (error) {
        useAlert('Failed to load profile data');
      } finally {
        this.isLoading = false;
      }
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file type
        if (!['image/jpeg', 'image/png'].includes(file.type)) {
          useAlert('Please upload a JPG or PNG file');
          return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          useAlert('File size should be less than 5MB');
          return;
        }

        this.profilePicture = file;
        // Create preview URL
        const reader = new FileReader();
        reader.onload = e => {
          this.profilePicturePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },

    async handleSubmit() {
      this.isSaving = true;
      try {
        const updateData = {};

        // Upload profile picture first if provided
        if (this.profilePicture) {
          const { fileUrl } = await uploadFile(
            this.profilePicture,
            this.accountId
          );
          updateData.profile_picture_url = fileUrl;
        }

        if (this.businessAddress) {
          updateData.business_address = this.businessAddress;
        }
        if (this.businessEmail) {
          updateData.business_email = this.businessEmail;
        }
        if (this.businessAbout) {
          updateData.business_about = this.businessAbout;
        }
        if (this.businessDescription) {
          updateData.business_description = this.businessDescription;
        }

        await updateWhatsAppProfile(this.accountId, this.inbox.id, updateData);
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_PROFILE.UPDATE_SUCCESS'));

        // Refresh profile data
        await this.fetchProfileData();
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.WHATSAPP_PROFILE.UPDATE_ERROR'));
      } finally {
        this.isSaving = false;
      }
    },

    getQualityRatingColor(rating) {
      switch (rating) {
        case 'GREEN':
          return 'text-green-600';
        case 'YELLOW':
          return 'text-yellow-600';
        case 'RED':
          return 'text-red-600';
        default:
          return 'text-gray-600';
      }
    },

    formatAccountStatus(status) {
      if (!status) return 'N/A';
      // Convert API status to human-readable format
      const statusMap = {
        ACTIVE: 'Active',
        PENDING: 'Pending',
        EXPIRED: 'Expired',
        APPROVED: 'Approved',
        PENDING_REVIEW: 'Pending Review',
        RESTRICTED: 'Restricted',
      };
      return statusMap[status] || status;
    },

    getAccountStatusColor(status) {
      if (!status) return 'text-gray-600';
      const colorMap = {
        ACTIVE: 'text-green-600',
        APPROVED: 'text-green-600',
        PENDING: 'text-yellow-600',
        PENDING_REVIEW: 'text-yellow-600',
        EXPIRED: 'text-red-600',
        RESTRICTED: 'text-red-600',
      };
      return colorMap[status] || 'text-gray-600';
    },

    formatIndustry(industry) {
      if (!industry) return 'N/A';

      const industryMap = {
        AUTOMOTIVE: 'Automotive',
        BEAUTY: 'Beauty',
        CONSULTING: 'Consulting',
        EDUCATION: 'Education',
        ENTERTAINMENT: 'Entertainment',
        EVENT_PLANNING: 'Event Planning',
        FASHION: 'Fashion',
        FINANCE: 'Finance',
        FOOD_BEVERAGE: 'Food & Beverage',
        PUBLIC_SERVICE: 'Public Service',
        HOTEL_LODGING: 'Hotel & Lodging',
        MEDICAL: 'Medical',
        OTHER: 'Other',
        PROFESSIONAL_SERVICES: 'Professional Services',
        PROF_SERVICES: 'Professional Services',
        REAL_ESTATE: 'Real Estate',
        RESTAURANT: 'Restaurant',
        RETAIL: 'Retail',
        SHOPPING: 'Shopping',
        TECHNOLOGY: 'Technology',
        TELECOMMUNICATIONS: 'Telecommunications',
        TRAVEL: 'Travel',
      };

      return industryMap[industry] || industry;
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <div v-if="isLoading" class="flex items-center justify-center py-8">
      <span class="text-gray-600">{{
        $t('INBOX_MGMT.WHATSAPP_PROFILE.LOADING')
      }}</span>
    </div>

    <div v-else>
      <!-- WhatsApp Account Overview Section -->
      <SettingsSection
        :title="$t('INBOX_MGMT.WHATSAPP_PROFILE.ACCOUNT_OVERVIEW_TITLE')"
      >
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.DISPLAY_NAME') }}
            </label>
            <p class="text-gray-900">{{ profile.display_name || 'N/A' }}</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.CONNECTED_NUMBER') }}
            </label>
            <p class="text-gray-900">{{ profile.phone_number || 'N/A' }}</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.QUALITY_RATING') }}
            </label>
            <p
              class="font-semibold"
              :class="getQualityRatingColor(profile.quality_rating)"
            >
              {{ profile.quality_rating || 'N/A' }}
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.ACCOUNT_STATUS') }}
            </label>
            <p
              class="font-semibold"
              :class="getAccountStatusColor(profile.account_status)"
            >
              {{ formatAccountStatus(profile.account_status) }}
            </p>
          </div>

          <div v-if="profile.business_verification_status">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{
                $t('INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_VERIFICATION_STATUS')
              }}
            </label>
            <p class="text-gray-900">
              {{ profile.business_verification_status }}
            </p>
          </div>

          <div v-if="profile.country">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.COUNTRY') }}
            </label>
            <p class="text-gray-900">{{ profile.country }}</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.MESSAGE_LIMITS') }}
            </label>
            <p class="text-gray-900">
              {{ profile.messaging_limit_tier || 'N/A' }}
            </p>
          </div>

          <div v-if="profile.business_account_id">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_ACCOUNT_ID') }}
            </label>
            <p class="text-gray-900">{{ profile.business_account_id }}</p>
          </div>

          <div v-if="profile.business_industry">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_INDUSTRY') }}
            </label>
            <p class="text-gray-900">
              {{ formatIndustry(profile.business_industry) }}
            </p>
          </div>
        </div>
      </SettingsSection>

      <!-- Business Profile Settings Section -->
      <SettingsSection
        :title="$t('INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_PROFILE_TITLE')"
      >
        <form @submit.prevent="handleSubmit">
          <!-- Profile Picture -->
          <div class="mb-6">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.PROFILE_PICTURE') }}
            </label>
            <p class="text-sm text-gray-500 mb-2">
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.PROFILE_PICTURE_DESC') }}
            </p>
            <div class="flex items-center gap-4">
              <div
                class="border-2 border-dashed border-gray-300 rounded-lg p-4 w-32 h-32 flex items-center justify-center"
              >
                <img
                  v-if="profilePicturePreview || profile.profile_picture_url"
                  :src="profilePicturePreview || profile.profile_picture_url"
                  alt="Profile"
                  class="w-full h-full object-cover rounded-lg"
                />
                <span v-else class="text-gray-400 text-4xl">{{
                  $t('INBOX_MGMT.WHATSAPP_PROFILE.NO_IMAGE')
                }}</span>
              </div>
              <div>
                <label for="profile-picture" class="cursor-pointer">
                  <input
                    id="profile-picture"
                    type="file"
                    accept="image/jpeg,image/png"
                    class="hidden"
                    @change="handleFileChange"
                  />
                  <span
                    class="text-sm text-blue-600 hover:text-blue-800 cursor-pointer"
                  >
                    {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.CHOOSE_FILE') }}
                  </span>
                </label>
                <p class="text-xs text-gray-500 mt-1">
                  {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.IMAGE_REQUIREMENTS') }}
                </p>
              </div>
            </div>
          </div>

          <!-- Business Address -->
          <div class="mb-6">
            <label
              for="business-address"
              class="block text-sm font-medium text-gray-700 mb-1"
            >
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_ADDRESS') }}
            </label>
            <p class="text-sm text-gray-500 mb-2">
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_ADDRESS_DESC') }}
            </p>
            <woot-input
              id="business-address"
              v-model="businessAddress"
              type="text"
              :placeholder="
                $t('INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_ADDRESS_PLACEHOLDER')
              "
            />
          </div>

          <!-- Business Email -->
          <div class="mb-6">
            <label
              for="business-email"
              class="block text-sm font-medium text-gray-700 mb-1"
            >
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_EMAIL') }}
            </label>
            <p class="text-sm text-gray-500 mb-2">
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_EMAIL_DESC') }}
            </p>
            <woot-input
              id="business-email"
              v-model="businessEmail"
              type="email"
              :placeholder="
                $t('INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_EMAIL_PLACEHOLDER')
              "
            />
          </div>

          <!-- Business About (Short description - 139 chars) -->
          <div class="mb-6">
            <label
              for="business-about"
              class="block text-sm font-medium text-gray-700 mb-1"
            >
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_ABOUT') }}
            </label>
            <p class="text-sm text-gray-500 mb-2">
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_ABOUT_DESC') }}
            </p>
            <textarea
              id="business-about"
              v-model="businessAbout"
              rows="3"
              maxlength="139"
              class="w-full px-3 py-2 border border-gray-300 rounded-md"
              :placeholder="
                $t('INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_ABOUT_PLACEHOLDER')
              "
            />
            <div class="mt-1 flex justify-between text-sm">
              <span
                :class="
                  businessAbout.length > maxAboutLength
                    ? 'text-red-600'
                    : 'text-gray-500'
                "
              >
                {{
                  $t('INBOX_MGMT.WHATSAPP_PROFILE.ABOUT_CHAR_COUNT', {
                    count: businessAbout.length,
                  })
                }}
              </span>
            </div>
          </div>

          <!-- Business Description (Full description - 512 chars) -->
          <div class="mb-6">
            <label
              for="business-description"
              class="block text-sm font-medium text-gray-700 mb-1"
            >
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_DESCRIPTION') }}
            </label>
            <p class="text-sm text-gray-500 mb-2">
              {{ $t('INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_DESCRIPTION_DESC') }}
            </p>
            <textarea
              id="business-description"
              v-model="businessDescription"
              rows="6"
              maxlength="512"
              class="w-full px-3 py-2 border border-gray-300 rounded-md"
              :placeholder="
                $t(
                  'INBOX_MGMT.WHATSAPP_PROFILE.BUSINESS_DESCRIPTION_PLACEHOLDER'
                )
              "
            />
            <div class="mt-1 flex justify-between text-sm">
              <span v-if="descriptionWarning" class="text-red-600">
                {{ descriptionWarning }}
              </span>
              <span
                v-else
                :class="
                  descriptionCharacterCount > maxDescriptionLength
                    ? 'text-red-600'
                    : 'text-gray-500'
                "
              >
                {{
                  $t('INBOX_MGMT.WHATSAPP_PROFILE.DESCRIPTION_CHAR_COUNT', {
                    count: descriptionCharacterCount,
                  })
                }}
              </span>
            </div>
          </div>

          <!-- Save Button -->
          <div class="flex justify-end">
            <NextButton
              type="submit"
              :is-loading="isSaving"
              :label="$t('INBOX_MGMT.WHATSAPP_PROFILE.SAVE')"
            />
          </div>
        </form>
      </SettingsSection>
    </div>
  </div>
</template>
